import { useEffect, useRef } from "react";

import { config } from "../config";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact";
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * Cloudflare Turnstile widget.
 *
 * Renders nothing (and silently no-ops) when VITE_TURNSTILE_SITE_KEY is
 * unset, so local dev works without a key. When the env var is set, the
 * widget loads the Turnstile script once, mounts a single instance, and
 * calls `onVerify(token)` when the user clears the challenge. The same
 * `onVerify(null)` is fired on expiry/error so the form can re-disable
 * its submit button.
 */
interface TurnstileProps {
  onVerify: (token: string | null) => void;
  onStatus?: (status: TurnstileStatus) => void;
  className?: string;
}

export type TurnstileStatus = "loading" | "ready" | "verified" | "error" | "expired";

let scriptLoading: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptLoading) {
    return scriptLoading;
  }
  scriptLoading = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => {
          existing.remove();
          reject(new Error("turnstile script failed"));
        },
        { once: true },
      );
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error("turnstile script failed"));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    scriptLoading = null;
    throw error;
  });
  return scriptLoading;
}

export default function Turnstile({ onVerify, onStatus, className }: TurnstileProps) {
  const siteKey = config.turnstileSiteKey;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    onStatus?.("loading");

    loadTurnstileScript()
      .then(() => {
        if (cancelled) return;
        const ts = window.turnstile;
        const container = containerRef.current;
        if (!ts || !container) return;
        widgetIdRef.current = ts.render(container, {
          sitekey: siteKey,
          theme: "dark",
          callback: (token: string) => {
            onVerify(token);
            onStatus?.("verified");
          },
          "error-callback": () => {
            onVerify(null);
            onStatus?.("error");
          },
          "expired-callback": () => {
            onVerify(null);
            onStatus?.("expired");
          },
        });
        onStatus?.("ready");
      })
      .catch(() => {
        if (!cancelled) {
          onVerify(null);
          onStatus?.("error");
        }
      });

    return () => {
      cancelled = true;
      const ts = window.turnstile;
      if (ts && widgetIdRef.current) {
        ts.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // We intentionally only re-run if the site key changes; callback props
    // are expected to be stable from the caller's useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) {
    return null;
  }
  return <div ref={containerRef} className={className} />;
}
